import * as React from "react";
import * as moment from "moment";
import { observer, inject } from "mobx-react";
import { UncontrolledTooltip } from "reactstrap";

import BootstrapTable from "react-bootstrap-table-next";

import { EditableInsuranceRecord, EditableAdHocInsuranceCompany, EditableAddressFromModel } from "src/components/customer/Insurance";

import { AttachmentLogEndpoints } from "src/endpoints/AttachmentLogEndpoints";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";

import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { CustomerStore } from "src/stores/CustomerStore";
import { SessionStore } from "src/stores/SessionStore";

import { InsuranceRecord, InsuranceCompany, SimpleCustomer, Customer } from "src/model/Customer";

import { InsuranceRecordRequest, AdHocInsuranceCompanyRequest } from "src/requests/CustomerRequests";
import { FileUploadRequest, FileUploadRequestFromAttachmentFile } from "src/requests/CommonRequest";
import { AddressRequest, EditableAddress } from "src/requests/CommonRequest";

import { InputGroup, TextareaGroup, SelectGroup } from "../common/SimpleFormComponents";
import { successMessage, errorMessage } from "../common/Toast";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";

import * as fileUtil from "../../common/Methods/FileUtil";

import AttachmentsForm from "../common/AttachmentsForm";
import { FormDialog } from "../common/FormDialog";

export interface Props {
    configurationStore?: ConfigurationStore;
    customerEndpoints?: CustomerEndpoints;

    showInsuranceRecordDialog: boolean;
    currentCustomer: SimpleCustomer | null;
    closeInsuranceDialog: () => void;
    onInsuranceRecordCreated: (updatedCustomer: Customer, insuranceRecord: InsuranceRecord) => void;
}

export interface PageState {
    wasValidated: boolean;
    loading: boolean;
}

type State = PageState & EditableInsuranceRecord.Fields & EditableAdHocInsuranceCompany.Fields & EditableAddress.Fields;

const DefaultState = (): State => {
    return {
        ...EditableInsuranceRecord.Empty(),
        ...EditableAdHocInsuranceCompany.Empty(),
        ...EditableAddress.Empty(),
        wasValidated: false,
        loading: false
    };
};

@inject("configurationStore")
@inject("customerEndpoints")
@observer
export default class CheckoutInsuranceComponent extends React.Component<Props, State> {
    state: State = DefaultState();

    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
    }

    handleOnChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>): void => {
        event.preventDefault();
        this.setState({ [event.target.name]: event.target.value } as any);
    };

    onSelectInsuranceCompany = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const companyId = e.target.value === "" ? null : parseInt(e.target.value, 10);
        this.setState({ companyId });
        if (companyId === null) {
            this.setState({
                ...EditableAdHocInsuranceCompany.Empty(),
                ...EditableAddress.Empty()
            });
        } else {
            this.onSelectExistingCompany(companyId);
        }
    };

    onSelectExistingCompany = (companyId: number): void => {
        const configurationStore = this.props.configurationStore!;

        const insuranceCompany = configurationStore.insuranceCompanies.find(insurance => insurance.id === companyId);
        if (!insuranceCompany) {
            throw new Error("Could not find insurance company in onSelectExistingCompany: " + companyId);
        }

        this.setState({
            ...EditableAdHocInsuranceCompany.FromModel(insuranceCompany),
            ...EditableAddressFromModel(insuranceCompany)
        });
    };

    generateRequest = (): InsuranceRecordRequest => {
        const address = this.state.companyId === null ? EditableAddress.ToRequest(this.state) : undefined;
        const adHocRequest = this.state.companyId === null ? EditableAdHocInsuranceCompany.ToRequest(this.state, address) : undefined;
        const request = EditableInsuranceRecord.ToRequest(this.state, adHocRequest);

        return request;
    };

    createInsuranceRecord = async () => {
        try {
            if (this.props.currentCustomer === null) {
                throw new Error("Cannot execute createInsuranceRecord without a current customer.");
            }
            this.setState({ loading: true });
            const request = this.generateRequest();
            const customer = await this.props.customerEndpoints!.createInsuranceRecord(this.props.currentCustomer.id, request);
            const mostRecentInsurance = customer.insuranceRecords.filter(ir => ir.dateCancelled === null).sort((a, b) => b.id - a.id)[0];
            this.props.onInsuranceRecordCreated(customer, mostRecentInsurance);
            this.clearForm();
        } catch (error) {
            errorMessage(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    submit = (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        event.preventDefault();
        event.stopPropagation();

        if (
            this.form &&
            this.form.checkValidity() &&
            (this.state.companyId !== null || this.state.companyId !== "") &&
            this.state.businessName !== ""
        ) {
            this.createInsuranceRecord();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    clearForm = () => {
        this.setState(DefaultState());
    };

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            return <div>No Configuration Store found.</div>;
        }

        const insuranceCompanies = configurationStore.insuranceCompanies;

        const disableProvince: boolean =
            (this.state.companyId === null || this.state.companyId === null || this.state.companyId === null
                ? !(this.state.companyId === null && this.state.countryId !== null)
                : this.state.countryId !== null) || this.state.loading;

        const disableInsuranceCompanyFields = this.state.companyId !== null || this.state.loading;

        return (
            <FormDialog
                title="Add new Insurance Record"
                modalSize="modal-lg"
                show={this.props.showInsuranceRecordDialog}
                onClose={() => this.props.closeInsuranceDialog()}
            >
                <form
                    id="insurance-form"
                    ref={form => (this.form = form)}
                    className={"needs-validation mt-3" + (this.state.wasValidated ? " was-validated" : "")}
                    onSubmit={e => this.submit(e)}
                    noValidate={true}
                >
                    <div className="form-row mb-4">
                        <SelectGroup
                            label="Insurance Company:"
                            name="documentType"
                            isRequired={true}
                            className="col-md-12 col-lg-8"
                            disabled={this.state.loading}
                            isInvalid={this.state.wasValidated && (this.state.companyId === null || this.state.companyId === null)}
                            value={this.state.companyId || ""}
                            onChange={e => this.onSelectInsuranceCompany(e)}
                        >
                            {insuranceCompanies
                                .slice()
                                .sort((a: InsuranceCompany, b: InsuranceCompany) =>
                                    a.businessName.toLowerCase().localeCompare(b.businessName.toLowerCase())
                                )
                                .map((c: InsuranceCompany, i: number) => (
                                    <option key={c.id} value={c.id}>
                                        {c.businessName}
                                    </option>
                                ))}
                            <option value="">Other</option>
                        </SelectGroup>
                    </div>
                    <div className="form-row">
                        <InputGroup
                            label="Business Name:"
                            className="col-md-12 col-lg-4"
                            type="string"
                            name="businessName"
                            isRequired={true}
                            disabled={disableInsuranceCompanyFields}
                            value={this.state.businessName || ""}
                            onChange={this.handleOnChange}
                        />
                        <InputGroup
                            label="Address:"
                            className="col-md-12 col-lg-4"
                            type="string"
                            name="address"
                            isRequired={false}
                            disabled={disableInsuranceCompanyFields}
                            value={this.state.address || ""}
                            onChange={this.handleOnChange}
                        />
                        <InputGroup
                            label="City:"
                            className="col-md-12 col-lg-4"
                            type="string"
                            name="city"
                            isRequired={false}
                            disabled={disableInsuranceCompanyFields}
                            value={this.state.city || ""}
                            onChange={this.handleOnChange}
                        />
                    </div>
                    <div className="form-row mb-4">
                        <InputGroup
                            label="Postal Code:"
                            className="col-md-12 col-lg-4"
                            type="string"
                            name="postalCode"
                            isRequired={false}
                            disabled={disableInsuranceCompanyFields}
                            value={this.state.postalCode || ""}
                            onChange={this.handleOnChange}
                        />
                        <SelectGroup
                            label="Country:"
                            name="countryId"
                            isRequired={false}
                            className="col-md-12 col-lg-4"
                            disabled={disableInsuranceCompanyFields}
                            isInvalid={false}
                            value={this.state.countryId || ""}
                            onChange={e => this.setState({ countryId: parseInt(e.target.value, 10) })}
                        >
                            <option value=""> Select a country...</option>
                            {configurationStore.countries.map(country => (
                                <option key={country.id} value={country.id}>
                                    {country.name} ({country.abbreviation})
                                </option>
                            ))}
                        </SelectGroup>
                        <SelectGroup
                            label="Province:"
                            name="provinceId"
                            isRequired={false}
                            className="col-md-12 col-lg-4"
                            disabled={disableProvince}
                            isInvalid={false}
                            value={this.state.provinceId || ""}
                            onChange={e => this.setState({ provinceId: parseInt(e.target.value, 10) })}
                        >
                            <option value=""> Select a province...</option>
                            {configurationStore.provinces
                                .filter(province => province.countryId === this.state.countryId)
                                .map(province => (
                                    <option key={province.id} value={province.id}>
                                        {province.name} ({province.abbreviation})
                                    </option>
                                ))}
                        </SelectGroup>
                    </div>
                    <div className="form-row mb-4">
                        <InputGroup
                            label="Contact Name:"
                            className="col-md-12 col-lg-4"
                            type="string"
                            name="contactName"
                            isRequired={false}
                            disabled={disableInsuranceCompanyFields}
                            value={this.state.contactName || ""}
                            onChange={this.handleOnChange}
                        />
                        <InputGroup
                            label="Phone:"
                            className="col-md-12 col-lg-4"
                            type="string"
                            name="phone"
                            isRequired={false}
                            disabled={disableInsuranceCompanyFields}
                            value={this.state.phone || ""}
                            onChange={this.handleOnChange}
                        />
                        <InputGroup
                            label="Email:"
                            className="col-md-12 col-lg-4"
                            type="string"
                            name="email"
                            isRequired={false}
                            disabled={disableInsuranceCompanyFields}
                            value={this.state.email || ""}
                            onChange={this.handleOnChange}
                        />
                    </div>
                    {/* <div className="form-row">
                        <div className="form-group col-md-12">
                            <label htmlFor="files">Attach File:</label>
                            <AttachmentsForm
                                attachments={this.state.attachmentLog}
                                updateAttachmentLog={attachmentLog => this.setState({ attachmentLog })}
                            />
                        </div>
                    </div> */}
                    <div className="for-row text-right">
                        <div className="col-12 pr-0">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    this.props.closeInsuranceDialog();
                                }}
                                disabled={this.state.loading}
                            >
                                Cancel
                            </button>
                            <button type="submit" form="insurance-form" className="btn btn-primary btn-sm mr-0 ml-3" disabled={this.state.loading}>
                                Create Insurance Record
                            </button>
                        </div>
                    </div>
                </form>
            </FormDialog>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutInsuranceComponent.tsx